import { CategoriaEnum } from '@acme/auth'
import dayjs from 'dayjs'
import { PreLoading } from 'global/components/LoadingCustumer'
import { useAuth } from 'hooks'
import { queryClient } from 'providers/ReactQuery'
import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    Cliente,
    FuncionarioUsuario,
    type Pendencias,
    clientes,
    empresas,
    funcionario,
    local,
    pendenciasTermos,
} from 'service/api'
import { toast } from 'sonner'
import OverlayApresentacao, {
    IPasso,
} from 'templates/Apresentacao/OverlayApresentacao'
import { useStore } from '../store'
import ErrorBoundary from './ErrorBoundary'
import {
    ROTA_ACEITE_TERMOS,
    isRedirecionarParaAceite,
} from './redirecionarAceiteTermos'
import { isRedirecionarParaMeuPlano } from './redirecionarPlanoInativo'

type Props = {
    children: React.ReactNode
}

export const Preload: React.FC<Props> = ({ children }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { auth, decoded, funcionarioSelecionado } = useAuth()
    const selecionado: Partial<FuncionarioUsuario> =
        decoded?.categoria === CategoriaEnum.Funcionario
            ? funcionarioSelecionado
            : ({} as any)

    const cliente = useStore(state => state.customer)

    clientes.useFetchById(decoded?.cliente, {
        enabled: !!decoded?.cliente,
        onSuccess: data => {
            useStore.setState({ customer: data as unknown as Cliente })
        },
    })

    const setCompetence = useStore(state => state.setCompetencia)
    const [empresaId, empresa] = useStore(state => [
        state.companyId,
        state.company,
    ])

    const {
        data: companies,
        isFetched: isEmpresasFetched,
        refetch: refetchEmpresas,
    } = empresas.useFetch({
        enabled: auth && !!decoded?.cliente,
        onSuccess: companies => {
            if (companies?.length && empresaId === -1) {
                const company =
                    companies.find(c => c.id === selecionado?.empresaId) ??
                    companies[0]
                useStore.setState({ company, companyId: company.id })
            } else if (companies?.length && empresaId !== -1) {
                const index = companies.findIndex(c => c.id === empresaId)

                if (index !== -1) {
                    const company = companies[index]
                    useStore.setState({ company, companyId: company.id })
                } else {
                    const company = companies[0]
                    useStore.setState({ company, companyId: company.id })
                }
            }
        },
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    })

    // Controla a primeira empresa selecionada e sincroniza com o store
    // o controle deve ser feito aqui e não dentro dos Selects
    useEffect(() => {
        if (isEmpresasFetched && empresaId === -1) refetchEmpresas()
        if (isEmpresasFetched && empresaId > 0 && companies?.length) {
            const empresaAtual = companies.find(c => c.id === empresaId)
            const competencia =
                empresaAtual?.competenciaAtual ||
                companies[0].competenciaAtual ||
                dayjs().format('YYYY-MM')
            setCompetence(competencia)
            if (empresaAtual && useStore.getState().company?.id !== empresaId)
                useStore.setState({ company: empresaAtual })
        }
    }, [empresaId, empresa?.id])

    // Sincroniza a competência atual da empresa selecionada com o store
    useEffect(() => {
        if (!empresa?.competenciaAtual) return
        setCompetence(empresa)
    }, [empresa?.competenciaAtual])

    // `setDefaultOptions` substitui os defaults inteiros; sem mesclar, o
    // `refetchOnWindowFocus: false` do provider some e trocar de aba refaz tudo.
    const defaults = queryClient.getDefaultOptions()
    queryClient.setDefaultOptions({
        ...defaults,
        queries: {
            ...defaults.queries,
            enabled: auth && empresaId !== -1,
        },
    })

    const employeeId = useStore(state => state.employeeId)

    funcionario.useFetch({
        queryParams: { empresaId: empresaId, page: 1, perPage: 25 },
        enabled: auth && empresaId > 0,
        onSuccess: (response: any) => {
            const employees = response?.data ?? response
            // Não há funcionários para a empresa
            if (!employees?.length)
                return useStore.setState({
                    employeeId: -1,
                    employee: null,
                })

            // Não há funcionário selecionado, selecionar o primeiro ativo
            const byId = employees.find((it: any) => it.id === employeeId)
            const employee =
                byId ||
                employees.filter((it: any) => !it.dtRescisao).at(0) ||
                employees[0]
            useStore.getState().setEmployeeId(employee.id)
        },
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    })

    const locationId = useStore(state => state.locationId)

    local.useFetch({
        queryParams: { empresaId: empresaId },
        onSuccess: locations => {
            if (locations?.length && locationId === -1) {
                useStore.setState({
                    locationId: locations[0].id,
                    location: locations[0],
                })
            } else if (locations?.length && locationId !== -1) {
                const index = locations.findIndex(l => l.id === locationId)

                if (index !== -1) {
                    const location = locations[index]

                    useStore.setState({
                        locationId: location.id,
                        location,
                    })
                }
            }
        },
    })

    useEffect(() => {
        if (!cliente || !decoded || [1, 7].includes(decoded?.categoria)) return
        if (!decoded.isAtivo) {
            let append = ''
            if (
                isRedirecionarParaMeuPlano({
                    categoria: decoded.categoria,
                    dtFimAtivacao: cliente.dtFimAtivacao,
                    pathname: location.pathname,
                })
            )
                navigate('/planos/me')
            else if (
                [CategoriaEnum.Cliente, CategoriaEnum.Avaliacao].includes(
                    decoded.categoria,
                ) &&
                dayjs(cliente.dtFimAtivacao).isAfter(dayjs())
            )
                append = 'Visite "Meu plano" para saber mais.'
            toast.warning(
                `Seu plano está inativo, por isso limitamos algumas funcionalidades. ${append}`,
            )
        }
    }, [cliente, cliente?.status, decoded?.isAtivo, decoded?.categoria])

    // O servidor responde `isDono: false` às demais categorias; o filtro só poupa a requisição.
    const { data: pendencias } = pendenciasTermos.useFetch<Pendencias>(false, {
        enabled:
            auth &&
            !!decoded &&
            [
                CategoriaEnum.Cliente,
                CategoriaEnum.Avaliacao,
                CategoriaEnum.Representante,
            ].includes(decoded.categoria),
    })

    // Depois do efeito de plano inativo para vencer o redirect dele: o aceite vem antes do plano.
    useEffect(() => {
        if (
            isRedirecionarParaAceite({
                pendencias,
                pathname: location.pathname,
            })
        )
            navigate(ROTA_ACEITE_TERMOS, { replace: true })
    }, [pendencias, location.pathname])

    const MemoizedOverlay = React.useMemo(
        () =>
            !decoded?.categoria ||
            ![CategoriaEnum.Cliente, CategoriaEnum.Avaliacao].includes(
                decoded.categoria,
            ) ? null : (
                <OverlayApresentacao
                    name="inicial"
                    passos={passosApresentacao}
                />
            ),
        [decoded?.categoria],
    )
    const usuarioLogado = useRef(decoded?.sub)

    useEffect(() => {
        if (!usuarioLogado.current || !decoded?.sub) {
            usuarioLogado.current = decoded?.sub
            return
        }

        if (usuarioLogado.current !== decoded.sub) {
            queryClient.clear()
            refetchEmpresas()
            usuarioLogado.current = decoded.sub
        }
    }, [decoded?.sub])

    return (
        <ErrorBoundary>
            <React.Suspense fallback={<PreLoading />}>
                {MemoizedOverlay}
                {children}
            </React.Suspense>
        </ErrorBoundary>
    )
}

const isMobilePresentation = window.innerWidth <= 1024 // lg

const passosApresentacao: IPasso[] = [
    {
        title: 'Cadastre sua empresa',
        description: `Cadastre sua empresa clicando em "Adicionar" e preenchendo os dados. 
        Depois, clique em "CADASTRAR".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Empresas_mobile')!
                : document.getElementById('sub-botao_Empresas')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Cadastros_mobile-trigger',
                      )!,
              ]
            : [() => document.getElementById('botao_Cadastros-trigger')!],
    },
    {
        title: 'Crie as regras de ponto',
        description: `Registre regras de cálculo para sua empresa, incluindo banco de horas, 
            compensação e porcentagens de pagamento. Digite um nome para a regra e 
            clique em "Salvar".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Regras de cálculo_mobile')!
                : document.getElementById('sub-botao_Regras de cálculo')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Cadastros_mobile-trigger',
                      )!,
              ]
            : [() => document.getElementById('botao_Cadastros-trigger')!],
    },
    {
        title: 'Crie as tabelas de horario',
        description: `Cadastre tabelas de horário, informando nome, tolerâncias e 
            horários de entrada e saída. Clique em "Salvar".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById(
                      'sub-botao_Tabela de horários_mobile',
                  )!
                : document.getElementById('sub-botao_Tabela de horários')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Cadastros_mobile-trigger',
                      )!,
              ]
            : [() => document.getElementById('botao_Cadastros-trigger')!],
    },
    {
        title: 'Cadastre os funcionários',
        description: `Cadastre funcionários clicando em "Adicionar" e preenchendo os dados. Clique em "Salvar".`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Funcionários_mobile')!
                : document.getElementById('sub-botao_Funcionários')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Cadastros_mobile-trigger',
                      )!,
              ]
            : [() => document.getElementById('botao_Cadastros-trigger')!],
    },
    {
        title: 'Registre o ponto manualmente',
        description: `Registre o ponto manualmente selecionando funcionário, empresa, data e horários.`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Digitação_mobile')!
                : document.getElementById('sub-botao_Digitação')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Manutenção do ponto_mobile-trigger',
                      )!,
              ]
            : [
                  () =>
                      document.getElementById(
                          'botao_Manutenção do ponto-trigger',
                      )!,
              ],
    },
    {
        title: 'Importar registros',
        description: `Importe registros de ponto (AFD) caso utilize um relógio de ponto.`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_AFD_mobile')!
                : document.getElementById('sub-botao_AFD')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Integrações_mobile-trigger',
                      )!,
              ]
            : [() => document.getElementById('botao_Integrações-trigger')!],
    },
    {
        title: 'Baixar aplicativo',
        description: `Baixe o aplicativo para Android e iOS para acessar o ponto de qualquer lugar.`,
    },
    {
        title: 'Relógio de ponto',
        description: `Ou use um relógio de ponto para registrar o ponto dos funcionários, 
            entre em contato com o suporte para configuração.`,
    },
    {
        title: 'Calcule o ponto',
        description: `Por fim, calcule o ponto dos funcionários em massa 
            usando a opção de cálculo automático da Ponto Ágil.`,
        getHighlightElement: () =>
            isMobilePresentation
                ? document.getElementById('sub-botao_Cálculo do ponto_mobile')!
                : document.getElementById('sub-botao_Cálculo do ponto')!,
        parentElements: isMobilePresentation
            ? [
                  () => document.getElementById('botao_menuVertical')!,
                  () =>
                      document.getElementById(
                          'botao_Manutenção do ponto_mobile-trigger',
                      )!,
              ]
            : [
                  () =>
                      document.getElementById(
                          'botao_Manutenção do ponto-trigger',
                      )!,
              ],
    },
]
